import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { isProofAllowed } from '../utils/proofFormatters';
import SelectorSection from './SelectorSection';

// Selecting these virtual proofs implies their deps, which can't be unchecked while on.
const HIGH_CERTAINTY = 'pg_high_certainty';
const GEOFENCE = 'pg_region_geofence';
const GUEST_LIST = 'pg_guest_list';
const PRESENCE = 'pg_presence';
const PRESENCE_SCORING = 'pg_presence_scoring';
// Identity-document attributes, opt-in per link (redacted otherwise).
const DOCUMENT_KEYS = [
  'pg_document_type',
  'pg_issuing_country',
  'pg_verified_by_provider',
  'pg_verification_id',
];
const DEPENDENCIES = {
  // The step-up document check is gated on Legal Name being selected
  // (rather than forcing it on), so only the region dep is implied.
  [HIGH_CERTAINTY]: ['pg_region'],
  [GEOFENCE]: ['pg_region'],
  [GUEST_LIST]: ['name'],
  [PRESENCE_SCORING]: [PRESENCE],
  // Document attributes ride along with the government-ID proof, which mobile
  // only bundles when Legal Name is required.
  ...Object.fromEntries(DOCUMENT_KEYS.map((k) => [k, ['name']])),
};

// A dep is locked while any selected virtual proof requires it.
const lockedByDependency = (value, key) =>
  Object.entries(DEPENDENCIES).some(([proof, deps]) => value.includes(proof) && deps.includes(key));

// Compact vertical radio stack (one option per line, never wrapped), with a
// single instruction line beneath describing the selected option (or the
// gating hint when the whole stack is disabled). Individual options can be
// disabled (e.g. choices that would remove a locked proof).
const RadioStack = ({ value, onChange, options, disabled, instruction }) => (
  <Box sx={{ mb: 1 }}>
    <RadioGroup value={value} onChange={onChange}>
      {options.map((opt) => (
        <FormControlLabel
          key={opt.value}
          value={opt.value}
          sx={{ mr: 0 }}
          control={<Radio size="small" disabled={disabled || opt.disabled} sx={{ py: 0.5 }} />}
          label={<Typography variant="body2">{opt.title}</Typography>}
        />
      ))}
    </RadioGroup>
    {/* Reserve two caption lines so switching options doesn't shift the layout. */}
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'text.secondary', mt: 0.5, minHeight: 40 }}
    >
      {instruction}
    </Typography>
  </Box>
);

/**
 * Grouped required-proofs picker shared by link create/edit, invitations,
 * and the per-app Defaults section. `value` stays a flat proof-key array;
 * the Presence and Document Check radios are projections onto it.
 *
 * The three groups flow into columns as the container allows: single column
 * narrow, Presence/Location stacked left of Identity in dialogs, all three
 * across on wide pages. `applyToSlot` (the Apply To section) joins the grid,
 * spanning the bottom in dialogs and sitting under Presence/Location (left
 * of Identity) when wide.
 *
 * The `geofenceSlot` / `guestListSlot` editors expand under their checkbox
 * (Location and Identity respectively) while that proof is selected, so
 * callers keep ownership of the bounds/list state.
 */
export default function RequiredProofsSelector({
  value = [],
  onChange,
  disabled = false,
  lockedKeys = [],
  allowedProofs = null,
  geofenceSlot = null,
  guestListSlot = null,
  applyToSlot = null,
}) {
  const { t } = useTranslation('shared');

  // Document Details starts open when a document attribute is already selected.
  const [documentDetailsOpen, setDocumentDetailsOpen] = useState(() =>
    DOCUMENT_KEYS.some((k) => value.includes(k))
  );

  // Account cap: hide proofs the account isn't provisioned for, so the UI never offers what the server rejects.
  const allowed = (key) => isProofAllowed(key, allowedProofs);

  const isLocked = (key) => lockedKeys.includes(key) || lockedByDependency(value, key);

  const toggle = (key) => {
    const checked = value.includes(key);
    if (checked && isLocked(key)) return;

    let next = checked ? value.filter((k) => k !== key) : [...value, key];
    if (!checked) {
      (DEPENDENCIES[key] || []).forEach((dep) => {
        if (!next.includes(dep)) next = [...next, dep];
      });
    }
    // The document check is gated on Legal Name; dropping the name drops it.
    if (checked && key === 'name') {
      next = next.filter((k) => k !== HIGH_CERTAINTY);
    }
    onChange(next);
  };

  const setKeys = (add, remove) => {
    // Never strip a proof the caller locked (mirrors the toggle guard).
    const removable = remove.filter((key) => !lockedKeys.includes(key));
    let next = value.filter((k) => !removable.includes(k));
    // Restore any removed key that a still-selected virtual proof depends on.
    removable.forEach((key) => {
      if (!next.includes(key) && lockedByDependency(next, key)) next = [...next, key];
    });
    add.forEach((key) => {
      if (!next.includes(key)) next = [...next, key];
      (DEPENDENCIES[key] || []).forEach((dep) => {
        if (!next.includes(dep)) next = [...next, dep];
      });
    });
    onChange(next);
  };

  // Presence radio projection: scoring implies presence.
  const presenceChoice = value.includes(PRESENCE_SCORING)
    ? 'antiphishing'
    : value.includes(PRESENCE)
    ? 'human'
    : 'none';
  const handlePresenceChange = (event) => {
    const choice = event.target.value;
    if (choice === 'none') setKeys([], [PRESENCE, PRESENCE_SCORING]);
    else if (choice === 'human') setKeys([PRESENCE], [PRESENCE_SCORING]);
    else setKeys([PRESENCE, PRESENCE_SCORING], []);
  };

  const identityKeys = ['name', 'pg_photo_verification_url', 'phone_number', 'pg_age', GUEST_LIST].filter(allowed);
  const documentKeys = DOCUMENT_KEYS.filter(allowed);

  // Document-check radio projection, available once any identity attribute
  // (including a document detail) is selected. Step-up auto-checks Legal Name
  // (plus the region dep).
  const docCheckChoice = value.includes(HIGH_CERTAINTY) ? 'stepup' : 'standard';
  const docCheckEnabled =
    identityKeys.some((key) => value.includes(key)) ||
    documentKeys.some((key) => value.includes(key));
  const handleDocCheckChange = (event) => {
    if (event.target.value === 'stepup') setKeys([HIGH_CERTAINTY, 'name'], []);
    else setKeys([], [HIGH_CERTAINTY]);
  };

  // The editor slot renders inline directly under its checkbox: it is
  // shown/hidden by that checkbox, so the causality must stay visible.
  const proofCheckbox = (key, slot = null) => {
    if (!allowed(key)) return null;
    const locked = isLocked(key);
    const on = value.includes(key);
    return (
      <Box key={key}>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={on}
              onChange={() => toggle(key)}
              disabled={disabled || locked}
              sx={{ py: 0.5 }}
            />
          }
          label={<Typography variant="body2">{t(`proofLabels.${key}`)}</Typography>}
        />
        {slot && (
          <Collapse in={on} unmountOnExit>
            <Box sx={{ pl: 2, pt: 1, pb: 1 }}>{slot}</Box>
          </Collapse>
        )}
      </Box>
    );
  };

  const showPresence = allowed(PRESENCE);
  const showAntiPhishing = allowed(PRESENCE_SCORING);
  const showDocCheck = allowed(HIGH_CERTAINTY);
  const locationKeys = ['pg_region', GEOFENCE].filter(allowed);

  const presenceInstruction = {
    none: t('proofSelector.presenceNoneInstruction'),
    human: t('proofSelector.presenceHumanInstruction'),
    antiphishing: t('proofSelector.presenceAntiPhishingInstruction'),
  }[presenceChoice];

  const docCheckInstruction = !docCheckEnabled
    ? t('proofSelector.docCheckRequiresIdentity')
    : docCheckChoice === 'stepup'
    ? t('proofSelector.docCheckStepUpInstruction')
    : t('proofSelector.docCheckStandardInstruction');

  // Named-areas templates only work when all three groups render; accounts
  // capped below that fall back to the simple auto-fit flow.
  const showLocation = locationKeys.length > 0;
  const showIdentity = showDocCheck || identityKeys.length > 0 || documentKeys.length > 0;
  const useAreas = showPresence && showLocation && showIdentity;

  // The inline geofence/guest-list editors need real width: a section whose
  // editor is open spans the full grid, and the remaining sections auto-flow
  // (dense) around it. The approved named-areas layout applies whenever no
  // editor is open.
  const geofenceOpen = !!geofenceSlot && value.includes(GEOFENCE);
  const guestListOpen = !!guestListSlot && value.includes(GUEST_LIST);
  const anyEditorOpen = geofenceOpen || guestListOpen;

  const gridSx =
    useAreas && !anyEditorOpen
      ? {
          display: 'grid',
          gap: 2,
          alignItems: 'start',
          gridTemplateColumns: '1fr',
          gridTemplateAreas: applyToSlot
            ? '"presence" "location" "identity" "applyto"'
            : '"presence" "location" "identity"',
          '@container (min-width: 480px)': {
            gridTemplateColumns: '1fr 1fr',
            gridTemplateAreas: applyToSlot
              ? '"presence identity" "location identity" "applyto applyto"'
              : '"presence identity" "location identity"',
          },
          '@container (min-width: 760px)': {
            gridTemplateColumns: '1fr 1fr 1fr',
            gridTemplateAreas: applyToSlot
              ? '"presence location identity" "applyto applyto identity"'
              : '"presence location identity"',
          },
        }
      : {
          display: 'grid',
          gap: 2,
          alignItems: 'start',
          gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
          gridAutoFlow: 'dense',
        };

  // Section placement: named areas normally; explicit full-width spans for
  // sections hosting an open editor (and Apply To) in auto-flow mode.
  const sectionSx = (area, fullWidth = false) => {
    if (useAreas && !anyEditorOpen) return { gridArea: area };
    return fullWidth ? { gridColumn: '1 / -1' } : undefined;
  };

  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box sx={gridSx}>
      {showPresence && (
        <SelectorSection
          sx={sectionSx('presence')}
          title={t('proofSelector.groupPresence')}
          instruction={t('proofSelector.groupPresenceInstruction')}
        >
          <RadioStack
            value={presenceChoice}
            onChange={handlePresenceChange}
            disabled={disabled}
            instruction={presenceInstruction}
            // Choices that would remove a caller-locked proof are disabled.
            options={[
              {
                value: 'none',
                title: t('proofSelector.presenceNone'),
                disabled: lockedKeys.includes(PRESENCE) || lockedKeys.includes(PRESENCE_SCORING),
              },
              {
                value: 'human',
                title: t('proofSelector.presenceHuman'),
                disabled: lockedKeys.includes(PRESENCE_SCORING),
              },
              ...(showAntiPhishing
                ? [{ value: 'antiphishing', title: t('proofSelector.presenceAntiPhishing') }]
                : []),
            ]}
          />
        </SelectorSection>
      )}

      {showLocation && (
        <SelectorSection
          sx={sectionSx('location', geofenceOpen)}
          title={t('proofSelector.groupLocation')}
          instruction={t('proofSelector.groupLocationInstruction')}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {locationKeys.map((key) => proofCheckbox(key, key === GEOFENCE ? geofenceSlot : null))}
          </Box>
        </SelectorSection>
      )}

      {showIdentity && (
        <SelectorSection
          sx={sectionSx('identity', guestListOpen)}
          title={t('proofSelector.groupIdentity')}
          instruction={t('proofSelector.groupIdentityInstruction')}
        >
          {showDocCheck && (
            <RadioStack
              value={docCheckChoice}
              onChange={handleDocCheckChange}
              disabled={disabled || !docCheckEnabled}
              instruction={docCheckInstruction}
              options={[
                {
                  value: 'standard',
                  title: t('proofSelector.docCheckStandard'),
                  disabled: lockedKeys.includes(HIGH_CERTAINTY),
                },
                { value: 'stepup', title: t('proofSelector.docCheckStepUp') },
              ]}
            />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {identityKeys.map((key) => proofCheckbox(key, key === GUEST_LIST ? guestListSlot : null))}
          </Box>
          {documentKeys.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setDocumentDetailsOpen((o) => !o)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDocumentDetailsOpen((o) => !o);
                  }
                }}
                aria-expanded={documentDetailsOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: 'text.secondary',
                }}
              >
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: documentDetailsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: (theme) => theme.transitions.create('transform', { duration: 150 }),
                  }}
                />
                <Typography variant="body2">{t('proofSelector.documentDetails')}</Typography>
              </Box>
              <Collapse in={documentDetailsOpen} unmountOnExit>
                <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2 }}>
                  {documentKeys.map((key) => proofCheckbox(key))}
                </Box>
              </Collapse>
            </Box>
          )}
        </SelectorSection>
      )}

      {applyToSlot && (
        <Box sx={sectionSx('applyto', true)}>{applyToSlot}</Box>
      )}
      </Box>
    </Box>
  );
}
